import React, { useEffect, useState } from "react";
import Select, { components, createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';

// "Cadastrar novo" é uma opção de verdade, e não um botão no rodapé, para as
// setas e o Enter chegarem nele. O aviso de lista vazia vira opção desabilitada
// porque, com a de cadastro na lista, o react-select não mostra mais o
// NoOptionsMessage.
const CREATE_OPTION = '__create__';
const HINT_OPTION = '__hint__';
const defaultFilter = createFilter();

const getCustomStyles = (hasLabel, highlighted, hasCreate) => ({
    control: (provided, state) => ({
      ...provided,
      backgroundColor: highlighted && !state.isFocused ? '#EDEAF7' : provided.backgroundColor,
      minHeight: '45px',
      borderRadius: '8px',
      fontSize: hasLabel ? '15px' : '13px',
      borderColor: state.isFocused ? '#4D3DAB' : highlighted ? '#A89EDF' : '#DDD9F0',
      borderWidth: '1px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(77, 61, 171, 0.16)' : 'none',
      transition: 'border-color 150ms ease, box-shadow 150ms ease',
      '&:hover': {
        borderColor: state.isFocused ? '#4D3DAB' : '#A89EDF',
      },
    }),
    valueContainer: provided => ({
      ...provided,
      minHeight: hasLabel ? '43px' : provided.minHeight,
      padding: hasLabel ? '19px 15px 4px' : provided.padding,
      alignItems: hasLabel ? 'flex-start' : provided.alignItems,
    }),
    singleValue: provided => ({
      ...provided,
      margin: 0,
      color: '#1A1530',
      fontSize: hasLabel ? '15px' : provided.fontSize,
    }),
    input: provided => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    menu: provided => ({
      ...provided,
      zIndex: 10,
      borderRadius: '12px',
      border: '1px solid #DDD9F0',
      boxShadow: '0 4px 12px rgba(26, 21, 48, 0.08), 0 2px 4px rgba(26, 21, 48, 0.04)',
      overflow: 'hidden',
    }),
    menuList: provided => (hasCreate ? { ...provided, paddingBottom: 0 } : provided),
    option: (provided, state) => {
      if (state.data?.value === CREATE_OPTION) return {
        ...provided,
        position: 'sticky',
        bottom: 0,
        fontSize: '13px',
        fontWeight: 600,
        color: '#4D3DAB',
        backgroundColor: state.isFocused ? '#F5F4FB' : '#fff',
        borderTop: '1px solid #EEECF7',
        cursor: 'pointer',
      };
      if (state.data?.value === HINT_OPTION) return {
        ...provided,
        fontSize: '13px',
        color: '#7A7499',
        textAlign: 'center',
        backgroundColor: 'transparent',
        cursor: 'default',
      };
      return {
      ...provided,
      fontSize: '13px',
      backgroundColor: state.isSelected ? '#4D3DAB' : state.isFocused ? '#F5F4FB' : 'transparent',
      color: state.isSelected ? '#fff' : '#1A1530',
      ':active': {
        backgroundColor: state.isSelected ? '#4D3DAB' : '#EDEAF7',
      },
      };
    },
    multiValue: provided => ({
      ...provided,
      backgroundColor: '#EDEAF7',
      borderRadius: '6px',
    }),
    placeholder: provided => ({
      ...provided,
      color: '#9B9B9B',
      margin: 0,
      fontSize: hasLabel ? '15px' : provided.fontSize,
    }),
})

export const InputSelect = ({label, value, options, change, error, isMulti, isDisabled, loading, noOptionsText, setSearch, removeMargin, filterOption, formatOptionLabel, styles, menuInBody, creatable, createLabel, highlighted, hint, onCreate, createText}) => {
    const [option, setOption] = useState(null);
    const hasLabel = Boolean(label);

    // creatable: a lista é sugestão, não limite — o usuário pode digitar um
    // valor que não está nela (ex.: código de serviço que a prefeitura
    // numera do próprio jeito).
    const SelectComponent = creatable ? CreatableSelect : Select;

    /**
     * Valor selecionado no modo simples. Sem isto, um valor fora da lista
     * (só possível no creatable) não casaria com nenhuma opção e o campo
     * apareceria VAZIO, como se nada estivesse salvo.
     */
    const getSingleValue = () => {
        const found = (options ?? []).filter(opt => opt.value === value);

        if (found.length > 0) return found;

        if (creatable && value !== null && value !== undefined && value !== '') {
            return [{ label: String(value), value }];
        }

        return [];
    };

    useEffect(() => {
        if(!isMulti){
            let arr = options ? options.filter(opt => opt.value === value) : [];
            setOption(arr[0]);
        } else {
            let arr = options ? options.filter(opt => value.includes(opt.value)) : [];
            setOption(arr);
        }
    }, [value, options]);

    const selectOptions = () => {
        const base = options ?? [];

        if (!onCreate) return base;

        const hint = base.length === 0
            ? [{ value: HINT_OPTION, label: noOptionsText || 'Nenhum resultado', isDisabled: true }]
            : [];

        return [...hint, ...base, { value: CREATE_OPTION, label: `+ ${createText ?? 'Cadastrar novo'}` }];
    };

    const handleOnChange = value => {
        if(!isMulti){
            if (value?.value === CREATE_OPTION) {
                onCreate();
                return;
            }
            change(value.value);
            return;
        } else {
            if (value.some(option => option.value === CREATE_OPTION)) {
                onCreate();
                return;
            }
            let values = [];
            value.map(option => values.push(option.value));
            change(values);
        }
    }

    const NoOptionsMessage = props => {
        return (
          <components.NoOptionsMessage {...props}>
            <span className="custom-css-class">{noOptionsText ?? 'Nenhum resultado'}</span> 
          </components.NoOptionsMessage>
        );
    }

    const getFilterOption = () => {
        let filter;
        if (typeof filterOption === 'function') filter = filterOption;
        if (filterOption === true) filter = () => true;

        if (!onCreate) return filter;

        return (candidate, input) => (
            candidate.value === CREATE_OPTION
            || candidate.value === HINT_OPTION
            || (filter ?? defaultFilter)(candidate, input)
        );
    };

    return (
        <div className={`select-group${hasLabel ? ' has-label' : ''}${error ? ' has-error' : ''}`} style={removeMargin ? { margin: 0} : {}}>
            {hasLabel &&
                <label>{label}</label>
            }
            <SelectComponent
                isMulti={isMulti ? true : false}
                isDisabled={isDisabled || loading === true ? true : false}
                components={{ NoOptionsMessage }}
                placeholder={loading === true ? 'Carregando...' : 'Selecione...'}
                value={Array.isArray(option) ? option : getSingleValue()}
                options={selectOptions()}
                formatCreateLabel={creatable
                    ? (input => createLabel ? createLabel(input) : `Usar "${input}"`)
                    : undefined
                }
                formatOptionLabel={formatOptionLabel ?? (item => (
                    <div className="select-label">
                        {item?.image}
                        {item.label}
                    </div>
                ))}
                onInputChange={(value, action) => {
                    if(setSearch && action.action === 'input-change'){
                        setSearch(value)
                    }
                    }}
                onChange={handleOnChange}
                // menuInBody: o menu é montado no <body>, então containers com
                // overflow hidden (Box, modal, abas) não recortam a lista.
                menuPortalTarget={menuInBody ? document.body : undefined}
                menuPosition={menuInBody ? 'fixed' : undefined}
                styles={{
                    ...getCustomStyles(hasLabel, highlighted, Boolean(onCreate)),
                    ...(menuInBody ? { menuPortal: base => ({ ...base, zIndex: 9999 }) } : {}),
                    ...(styles || {}),
                }}
                filterOption={getFilterOption()}
            />
            {error &&
                <span className="error-message">{error}</span>
            }
            {!error && hint &&
                <span className="input-hint">{hint}</span>
            }
        </div>
    );
}

export default InputSelect;
